import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WalletContext } from '../context/WalletContex';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import PasswordInput from '../components/ui/PasswordInput';
import OtpModal from '../components/ui/OtpModal';
import Logo from '../assets/logo.png';
import { isPasswordValid } from '@ikhua/shared';
import apiClient from '../utils/apiClient';
import {
  HiArrowLeft,
  HiShieldCheck,
  HiEye,
  HiEyeSlash,
  HiEnvelope,
  HiCheckCircle,
} from 'react-icons/hi2';

const SCREEN = {
  HOME:     'home',
  REGISTER: 'register',
  LOGIN:    'login',
  FORGOT:   'forgot',
};

export default function Welcome() {
  const [screen, setScreen] = useState(SCREEN.HOME);
  if (screen === SCREEN.REGISTER) return <RegisterScreen onBack={() => setScreen(SCREEN.HOME)} />;
  if (screen === SCREEN.LOGIN)    return <LoginScreen onBack={() => setScreen(SCREEN.HOME)} onForgot={() => setScreen(SCREEN.FORGOT)} />;
  if (screen === SCREEN.FORGOT)   return <ForgotPasswordScreen onBack={() => setScreen(SCREEN.LOGIN)} />;
  return <HomeScreen onRegister={() => setScreen(SCREEN.REGISTER)} onLogin={() => setScreen(SCREEN.LOGIN)} />;
}

/* ─── Landing ─────────────────────────────────────────────── */
function HomeScreen({ onRegister, onLogin }) {
  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'space-between',
      padding: '60px 28px 48px', background: '#090817', maxWidth: 430, margin: '0 auto',
    }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
        <img src={Logo} alt="Ikhua" style={{ width: 80, height: 80, objectFit: 'contain' }} />
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '32px', fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-1px' }}>Ikhua</h1>
          <p style={{ fontSize: '15px', color: '#8B8FA8', marginTop: 8, lineHeight: 1.5 }}>
            Send, receive, swap and trade crypto — wherever you are.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          {[['BTC', 'btc'], ['USDT TRC20', 'usdt'], ['TRX', 'trx'], ['BNB', 'eth']].map(([label, cls]) => (
            <span key={label} className={`coin-icon ${cls}`} style={{ width: 'auto', padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700, height: 'auto' }}>
              {label}
            </span>
          ))}
        </div>
      </div>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Button variant="primary" size="lg" full onClick={onRegister}>Create Account</Button>
        <Button variant="secondary" size="lg" full onClick={onLogin}>Sign In</Button>
      </div>
    </div>
  );
}

/* ─── Register (custodial) ────────────────────────────────── */
function RegisterScreen({ onBack }) {
  const { registerWallet, isLoading } = useContext(WalletContext);
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [agreed, setAgreed]     = useState(false);
  const [error, setError]       = useState('');
  const [done, setDone]         = useState(false);

  const handleRegister = async () => {
    if (!agreed)                    { setError('You must agree to the Terms of Service'); return; }
    if (!email)                     { setError('Email is required'); return; }
    if (!isPasswordValid(password)) { setError('Password does not meet requirements'); return; }
    if (password !== confirm)       { setError('Passwords do not match'); return; }
    setError('');
    try {
      await registerWallet(email, password, displayName);
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Registration failed');
    }
  };

  if (done) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', maxWidth: 430, margin: '0 auto', background: '#090817', textAlign: 'center', gap: 20 }}>
        <div style={{ width: 72, height: 72, borderRadius: 24, background: 'rgba(124,92,246,0.12)', border: '1px solid rgba(124,92,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <HiEnvelope size={32} style={{ color: '#7C5CF6' }} />
        </div>
        <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#fff', margin: 0 }}>Check your email</h2>
        <p style={{ color: '#8B8FA8', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
          We sent a verification link to <strong style={{ color: '#fff' }}>{email}</strong>. Your wallet is ready — verify your email to unlock all features.
        </p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', padding: '60px 24px 48px', maxWidth: 430, margin: '0 auto', background: '#090817' }}>
      <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.07)', border: 'none', borderRadius: 12, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', marginBottom: 32 }}>
        <HiArrowLeft size={18} />
      </button>

      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ width: 64, height: 64, borderRadius: 22, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <HiShieldCheck size={28} style={{ color: '#10B981' }} />
        </div>
        <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>Create Account</h2>
        <p style={{ color: '#8B8FA8', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
          Your wallet is secured by Ikhua. Sign in from any device with just your email and password — no seed phrase needed.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label="Display name (optional)" placeholder="e.g. Kwame" value={displayName} onChange={e => setDisplayName(e.target.value.slice(0, 20))} hint="3–20 characters · shown to traders · cannot be changed after set" />
        <Input label="Email address" type="email" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} />
        <PasswordInput label="Password" value={password} onChange={e => setPassword(e.target.value)} />
        <Input label="Confirm password" type="password" placeholder="Re-enter password" value={confirm} onChange={e => setConfirm(e.target.value)} />

        <button
          onClick={() => setAgreed(a => !a)}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: agreed ? 'rgba(124,92,246,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${agreed ? 'rgba(124,92,246,0.3)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 14, padding: '12px 14px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
        >
          <div style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1, background: agreed ? '#7C5CF6' : 'transparent', border: `2px solid ${agreed ? '#7C5CF6' : 'rgba(255,255,255,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
            {agreed && <span style={{ color: '#fff', fontSize: 12, fontWeight: 900, lineHeight: 1 }}>✓</span>}
          </div>
          <span style={{ fontSize: '13px', color: '#8B8FA8', lineHeight: 1.6 }}>
            I have read and agree to the{' '}
            <span onClick={e => { e.stopPropagation(); navigate('/legal?tab=terms'); }} style={{ color: '#7C5CF6', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>Terms of Service</span>
            {' '}and{' '}
            <span onClick={e => { e.stopPropagation(); navigate('/legal?tab=privacy'); }} style={{ color: '#7C5CF6', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>Privacy Policy</span>
          </span>
        </button>

        {error && <p style={{ color: '#EF4444', fontSize: '13px', margin: 0 }}>{error}</p>}
      </div>

      <div style={{ marginTop: 'auto', paddingTop: 28 }}>
        <Button variant="primary" size="lg" full onClick={handleRegister} loading={isLoading}>Create Account</Button>
      </div>
    </div>
  );
}

/* ─── Sign In ─────────────────────────────────────────────── */
function LoginScreen({ onBack, onForgot }) {
  const { recoverWallet } = useContext(WalletContext);
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [unverified, setUnverified] = useState(false);
  const [locked, setLocked]         = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone]       = useState(false);

  const handleLogin = async () => {
    if (!email || !password) { setError('Please fill in all fields'); return; }
    setLoading(true); setError(''); setUnverified(false); setLocked(false); setResendDone(false);
    try {
      await recoverWallet(email, password);
    } catch (e) {
      const data = e.response?.data;
      if (e.response?.status === 423 || data?.locked) {
        setLocked(true);
        setError(data?.error || 'Account locked after too many failed attempts.');
      } else if (data?.unverified) {
        setUnverified(true);
        setError('Your email is not verified yet. Click the link we sent you, or resend it below.');
      } else {
        setError(data?.error || 'Sign in failed. Check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) { setError('Enter your email above first'); return; }
    setResendLoading(true);
    try {
      await apiClient.post('/wallet/resend-verification', { email });
      setResendDone(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not resend. Try again shortly.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', padding: '60px 24px 48px', maxWidth: 430, margin: '0 auto', background: '#090817' }}>
      <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.07)', border: 'none', borderRadius: 12, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', marginBottom: 32 }}>
        <HiArrowLeft size={18} />
      </button>

      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: '26px', fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>Sign In</h2>
        <p style={{ color: '#8B8FA8', fontSize: '14px', margin: 0 }}>Enter your email and password to access your wallet.</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Input label="Email address" type="email" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} />
        <Input
          label="Password"
          type={showPw ? 'text' : 'password'}
          placeholder="••••••••"
          value={password}
          onChange={e => setPassword(e.target.value)}
          right={
            <button onClick={() => setShowPw(p => !p)} style={{ background: 'none', border: 'none', color: '#4B5170', cursor: 'pointer', display: 'flex' }}>
              {showPw ? <HiEyeSlash size={18} /> : <HiEye size={18} />}
            </button>
          }
        />
        {error && <p style={{ color: '#EF4444', fontSize: '13px', margin: 0 }}>{error}</p>}
        {unverified && (
          resendDone
            ? <p style={{ color: '#10B981', fontSize: '13px', margin: 0 }}>Verification email resent — check your inbox.</p>
            : <button onClick={handleResend} disabled={resendLoading} style={{ background: 'rgba(124,92,246,0.1)', border: '1px solid rgba(124,92,246,0.3)', borderRadius: 10, color: '#A78BFA', fontSize: '13px', fontWeight: 600, cursor: resendLoading ? 'not-allowed' : 'pointer', padding: '10px 14px', textAlign: 'left', opacity: resendLoading ? 0.6 : 1 }}>
                {resendLoading ? 'Sending…' : 'Resend verification email'}
              </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 'auto', paddingTop: 32 }}>
        {locked
          ? <Button variant="primary" size="lg" full onClick={onForgot}>Reset Password to Unlock</Button>
          : <Button variant="primary" size="lg" full onClick={handleLogin} loading={loading}>Sign In</Button>
        }
        <button onClick={onForgot} style={{ background: 'none', border: 'none', color: '#4B5170', fontSize: '13px', cursor: 'pointer', padding: '6px', textDecoration: 'underline' }}>
          Forgot password?
        </button>
      </div>
    </div>
  );
}

/* ─── Forgot password ─────────────────────────────────────── */
function ForgotPasswordScreen({ onBack }) {
  const { forgotPassword } = useContext(WalletContext);
  const [email, setEmail]       = useState('');
  const [otpOpen, setOtpOpen]   = useState(false);
  const [newPw, setNewPw]       = useState('');
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [done, setDone]         = useState(false);
  const [isCustodial, setIsCustodial] = useState(null); // null = unknown, true/false = checked
  const [phrase, setPhrase]     = useState('');

  const checkAccount = async () => {
    if (!email) { setError('Enter your email first'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await apiClient.get(`/wallet/check-account?email=${encodeURIComponent(email)}`);
      setIsCustodial(data.custodial ?? true);
    } catch {
      // Always advance — never reveal if email is registered
      setIsCustodial(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOtp = async (otpCode) => {
    setOtpOpen(false);
    if (!isPasswordValid(newPw)) { setError('Password does not meet requirements'); return; }
    if (newPw !== confirm)       { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try {
      await forgotPassword(email, otpCode, newPw, true);
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLegacyReset = async () => {
    if (phrase.trim().split(/\s+/).length < 12) { setError('Please enter your 12-word recovery phrase'); return; }
    if (!isPasswordValid(newPw))   { setError('Password does not meet requirements'); return; }
    if (newPw !== confirm)         { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try {
      await forgotPassword(email, phrase, newPw, false);
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', maxWidth: 430, margin: '0 auto', background: '#090817', textAlign: 'center', gap: 20 }}>
        <div style={{ width: 72, height: 72, borderRadius: 24, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <HiCheckCircle size={32} style={{ color: '#10B981' }} />
        </div>
        <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#fff', margin: 0 }}>Password Reset!</h2>
        <p style={{ color: '#8B8FA8', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>Sign in with your new password to access your wallet.</p>
        <Button variant="primary" size="lg" full onClick={onBack}>Back to Sign In</Button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', padding: '60px 24px 48px', maxWidth: 430, margin: '0 auto', background: '#090817' }}>
      <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.07)', border: 'none', borderRadius: 12, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', marginBottom: 32 }}>
        <HiArrowLeft size={18} />
      </button>

      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: '26px', fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>Reset Password</h2>
        <p style={{ color: '#8B8FA8', fontSize: '14px', margin: 0, lineHeight: 1.6 }}>
          {isCustodial === null
            ? 'Enter your email to get started.'
            : isCustodial
              ? 'Enter your new password. We will send you a verification code to confirm.'
              : 'Enter your 12-word recovery phrase and a new password.'}
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label="Account email" type="email" placeholder="you@email.com" value={email} onChange={e => { setEmail(e.target.value); setIsCustodial(null); }} />

        {isCustodial === null && (
          <Button variant="primary" size="lg" full onClick={checkAccount} loading={loading}>Continue</Button>
        )}

        {isCustodial === true && (
          <>
            <PasswordInput label="New Password" value={newPw} onChange={e => setNewPw(e.target.value)} />
            <Input label="Confirm Password" type="password" placeholder="Re-enter" value={confirm} onChange={e => setConfirm(e.target.value)} />
            <Button variant="primary" size="lg" full onClick={() => {
              if (!isPasswordValid(newPw)) { setError('Password does not meet requirements'); return; }
              if (newPw !== confirm)       { setError('Passwords do not match'); return; }
              setError(''); setOtpOpen(true);
            }} loading={loading}>Send Verification Code</Button>
          </>
        )}

        {isCustodial === false && (
          <>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#8B8FA8', display: 'block', marginBottom: 6 }}>Recovery phrase (12 words)</label>
              <textarea value={phrase} onChange={e => setPhrase(e.target.value)} placeholder="word1 word2 word3 … word12" rows={3}
                style={{ width: '100%', background: '#1A1748', border: '1.5px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '12px 14px', color: '#fff', fontSize: '14px', fontFamily: 'monospace', resize: 'none', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6 }} />
            </div>
            <PasswordInput label="New Password" value={newPw} onChange={e => setNewPw(e.target.value)} />
            <Input label="Confirm Password" type="password" placeholder="Re-enter" value={confirm} onChange={e => setConfirm(e.target.value)} />
            <Button variant="primary" size="lg" full onClick={handleLegacyReset} loading={loading}>Reset Password</Button>
          </>
        )}

        {error && <p style={{ color: '#EF4444', fontSize: '13px', margin: 0 }}>{error}</p>}
      </div>

      <OtpModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        email={email}
        action="password_reset"
        onConfirm={handleConfirmOtp}
      />
    </div>
  );
}
